/**
 * Minimal Tiptap editor for the chat input — looks and feels like a textarea
 * but supports inline @mention nodes for context references.
 *
 * Registered as Alpine.data('chatEditor', ChatEditorComponent).
 */
import { Editor } from "@tiptap/core";
import { EditorState } from "@tiptap/pm/state";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import { contextReferenceSuggestion } from "./chat-mention-suggestion";

interface MentionRef {
  type: string;
  id: string;
  title: string;
}

export const ChatEditorComponent = (placeholder: string) => ({
  _editor: null as Editor | null,
  content: "",
  // The last submitted message (editor JSON, mentions included), kept until the
  // send is known to have been refused so restoreDraft() can put it back.
  _lastDraft: null as null | Record<string, unknown>,

  init() {
    const self = this as any;
    const container = self.$refs.chatEditorContainer as HTMLElement;

    // Read i18n labels from data attribute (set by Templ template)
    let refLabels: Record<string, string> = {};
    try {
      refLabels = JSON.parse(container.dataset.refLabels || "{}");
    } catch {
      /* ignore */
    }

    this._editor = new Editor({
      element: container,
      extensions: [
        Document,
        Paragraph,
        Text,
        HardBreak,
        Placeholder.configure({ placeholder }),
        Mention.configure({
          HTMLAttributes: {
            class: "context-ref-chip",
          },
          renderHTML({ node }) {
            return [
              "span",
              {
                class: "context-ref-chip",
                "data-type": "mention",
                "data-id": node.attrs.id,
                "data-label": node.attrs.label,
                "data-opens-drawer": "",
              },
              `@${node.attrs.label ?? node.attrs.id}`,
            ];
          },
          renderText({ node }) {
            // Parse type:id from the stored id
            const rawId = node.attrs.id || "";
            const colonIdx = rawId.indexOf(":");
            const refType = colonIdx > 0 ? rawId.slice(0, colonIdx) : "unknown";
            const refId = colonIdx > 0 ? rawId.slice(colonIdx + 1) : rawId;
            return `@[${node.attrs.label ?? rawId}](ref:${refType}:${refId})`;
          },
          suggestion: contextReferenceSuggestion(refLabels),
        }),
      ],
      editorProps: {
        attributes: {
          class:
            "w-full bg-transparent focus:outline-none resize-none min-h-[56px] max-h-64 overflow-y-auto p-4 pb-2 text-base-content placeholder:text-base-content/30 text-base",
        },
        handleKeyDown: (_view, event) => {
          // Mobile (< lg / soft keyboard): Enter inserts a newline; sending is
          // done via the send button. Desktop keeps Enter = submit, Shift+Enter =
          // newline. Return false so ProseMirror handles Enter as a line break.
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            window.matchMedia("(max-width: 1023px)").matches
          ) {
            return false;
          }
          // Enter submits (unless Shift, Ctrl, Meta, or IME composing)
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.isComposing
          ) {
            // Don't submit if the mention suggestion dropdown is open
            if (
              document.querySelector("[data-tippy-root]") ||
              document.querySelector(".suggestion-popup")
            ) {
              return false;
            }
            const text = this.getTextContent();
            if (text.trim()) {
              event.preventDefault();
              this.submit();
              return true;
            }
          }
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        this.content = editor.getText();
        // Auto-height: grow the ProseMirror element
        const el = container.querySelector(".ProseMirror") as HTMLElement;
        if (el) {
          el.style.height = "auto";
          el.style.height = Math.min(el.scrollHeight, 256) + "px";
        }
      },
    });

    // Click on mention chips → open reference preview drawer
    container.addEventListener("click", (e) => {
      const chip = (e.target as HTMLElement).closest('[data-type="mention"]') as HTMLElement | null;
      if (!chip) return;
      const rawId = chip.dataset.id || "";
      const colonIdx = rawId.indexOf(":");
      if (colonIdx <= 0) return;
      const refType = rawId.slice(0, colonIdx);
      const refId = rawId.slice(colonIdx + 1);
      e.preventDefault();
      chip.dispatchEvent(
        new CustomEvent("open-reference", {
          bubbles: true,
          detail: { type: refType, id: refId },
        }),
      );
    });
  },

  getTextContent(): string {
    if (!this._editor) return "";
    return this._editor.getText();
  },

  /**
   * Serialize the editor content to plain text, with mention nodes rendered
   * as @[Label](ref:type:id) syntax.
   */
  getSerializedContent(): string {
    if (!this._editor) return "";
    const doc = this._editor.state.doc;
    let result = "";

    doc.descendants((node, _pos, parent) => {
      if (node.type.name === "mention") {
        const rawId = node.attrs.id || "";
        const colonIdx = rawId.indexOf(":");
        const refType = colonIdx > 0 ? rawId.slice(0, colonIdx) : "unknown";
        const refId = colonIdx > 0 ? rawId.slice(colonIdx + 1) : rawId;
        const label = node.attrs.label ?? rawId;
        result += `@[${label}](ref:${refType}:${refId})`;
        return false; // Don't descend into mention children
      }
      if (node.type.name === "text") {
        result += node.text || "";
        return false;
      }
      if (node.type.name === "hardBreak") {
        result += "\n";
        return false;
      }
      if (node.type.name === "paragraph") {
        // Add newline between paragraphs (but not before the first)
        if (parent && parent.type.name === "doc") {
          const index = parent.content.content?.indexOf(node) ?? -1;
          if (index > 0) {
            result += "\n";
          }
        }
        return true; // Descend into children
      }
      return true;
    });

    return result;
  },

  /**
   * Extract all mention nodes as structured references.
   */
  getReferences(): MentionRef[] {
    if (!this._editor) return [];
    const refs: MentionRef[] = [];
    this._editor.state.doc.descendants((node) => {
      if (node.type.name === "mention") {
        const rawId = node.attrs.id || "";
        const colonIdx = rawId.indexOf(":");
        const refType = colonIdx > 0 ? rawId.slice(0, colonIdx) : "unknown";
        const refId = colonIdx > 0 ? rawId.slice(colonIdx + 1) : rawId;
        refs.push({
          type: refType,
          id: refId,
          title: node.attrs.label ?? rawId,
        });
      }
    });
    return refs;
  },

  /**
   * Submit: dispatch custom event with serialized content + extracted references.
   */
  submit() {
    const content = this.getSerializedContent();
    const references = this.getReferences();
    if (!content.trim()) return;
    this._lastDraft = this._editor ? this._editor.getJSON() : null;
    this.clear();
    (this as any).$dispatch("chat-submit", { content, references });
  },

  /**
   * Put the last submitted message back after a refused send (spec 2026-10-09
   * §5: "Client input survives"). Listens to `chat-restore-draft`, which the
   * chat form dispatches when its request failed or answered >= 400 (429 AI
   * paused / rate limited, 503 unavailable). Only the editor that submitted holds
   * a draft, so other chat editors on the page ignore the event.
   *
   * If the user already started typing again while the request was in flight,
   * the old message goes in front of the new text instead of replacing it.
   * Deferred a frame for the same "mismatched transaction" reason as
   * insertReferences().
   */
  restoreDraft() {
    if (!this._editor || !this._lastDraft) return;
    const doc = this._lastDraft;
    this._lastDraft = null;
    const editor = this._editor;
    editor.commands.focus("end");
    requestAnimationFrame(() => {
      if (editor.isEmpty) {
        // emitUpdate=true so onUpdate re-measures the height for a multi-line draft.
        editor.commands.setContent(doc, true);
      } else if (Array.isArray((doc as any).content)) {
        editor.commands.insertContentAt(0, (doc as any).content);
      }
    });
  },

  /**
   * Clear the editor by replacing the state entirely.
   *
   * We avoid commands.clearContent() because it dispatches a transaction,
   * and ProseMirror's view.dispatch() flushes pending DOM observations
   * first — which can advance the state and cause a "mismatched
   * transaction" error. Replacing the state via view.updateState()
   * bypasses the transaction system completely.
   */
  clear() {
    if (!this._editor) return;
    const view = this._editor.view;
    view.updateState(
      EditorState.create({
        schema: view.state.schema,
        plugins: view.state.plugins,
      }),
    );
    this.content = "";
    // Reset height
    const container = (this as any).$refs?.chatEditorContainer as HTMLElement | undefined;
    const el = container?.querySelector(".ProseMirror") as HTMLElement | undefined;
    if (el) {
      el.style.height = "56px";
    }
  },

  /**
   * Insert mention nodes from an external source (e.g., command palette).
   * Uses requestAnimationFrame to ensure the editor state is synced after
   * focus transitions from the modal.
   */
  insertReferences(refs: Array<{ type: string; id: string; label?: string; title?: string }>) {
    if (!this._editor) return;
    const editor = this._editor;
    // Focus first, then insert after a frame to avoid mismatched transaction
    editor.commands.focus("end");
    requestAnimationFrame(() => {
      for (const ref of refs) {
        editor
          .chain()
          .insertContent({
            type: "mention",
            attrs: {
              id: `${ref.type}:${ref.id}`,
              label: ref.label || ref.title || "",
            },
          })
          .insertContent(" ")
          .run();
      }
    });
  },

  /**
   * Replace the editor content with the given plain-text prompt and
   * focus the editor. Used by the wizard's FieldHelpButton, which
   * dispatches `wizard-chat-prepopulate` with `{prompt}` so the
   * operator can refine the suggested question before sending.
   *
   * We clear first to avoid concatenating onto stale content, then
   * insert the text after a frame to dodge the same "mismatched
   * transaction" pitfall that bites insertReferences().
   */
  prepopulate(text: string) {
    if (!this._editor || !text) return;
    const editor = this._editor;
    this.clear();
    editor.commands.focus("end");
    requestAnimationFrame(() => {
      editor.chain().insertContent(text).run();
    });
  },

  /**
   * Insert @ at cursor and trigger the mention suggestion.
   * Used by the + button.
   */
  insertAt() {
    if (!this._editor) return;
    const editorEl = this._editor.view.dom;
    editorEl.focus();
    setTimeout(() => {
      // execCommand('insertText') goes through the browser's native
      // contenteditable pipeline — ProseMirror picks it up correctly
      document.execCommand("insertText", false, "@");
    }, 50);
  },

  /**
   * Cleanup on component destroy.
   */
  destroy() {
    this._editor?.destroy();
    this._editor = null;
  },
});
